import { useEffect, useMemo } from 'react';

import ReactGA from 'react-ga4';
import Cookies from 'js-cookie';
import moment from 'moment/min/moment-with-locales';

import { IntlProvider } from 'react-intl';
import { get, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { ToastContainer } from 'react-toastify';
import { CookiesProvider } from 'react-cookie';

import { getAntLocale, getLocale, getMessages } from '@i18n';
import {
  ExpiredSessionModal,
  ApplicationHeader,
  ApplicationFooter,
  ApplicationHelmet,
  ApplicationHeaderMessage,
  ApplicationGoogleManager,
  CurrencyCalculator,
  ModernSidebarGlobal,
} from '@components';
import { useModernTemplateCheck } from '@components';
import { Loading } from '@share/components';
import { COOKIE1_TOKEN_NAME, COOKIE_TOKEN_NAME, LOGGED_USER_LABEL, Routes, USER_BASE_URL } from '@share/constants';
import {
  DecodeTokenData,
  getPreferenceFromAccount,
  isCondoWorldFromPath,
  isThreeDSFromPath,
  isCondoEngineFromPath,
  isCondoWeeksFromPath,
  isReservationCancellationFromPath,
  LoginType,
  getAccountUsernameFromPath,
  isSignOutFromPath,
  isAnyReservationCancelFromPath,
  getCancelIdCancellationFromPath,
  getTempKeyCancellationFromPath,
  FunctionalRootState,
  isSpanish,
  isDetailFromPath
} from '@share/utils';
import { isErrorFromPath, isSignInFromPath, isWidgetFromPath } from '@share/utils';
import {
  ILoginState,
  logout,
  IMenuState,
  setLoginRedirect,
  getLoginUserAccount,
} from '@share/store/slices';
import { LOGOUT_FIELD_KEY } from '@share/common-types';

import { ApplicationRoutes } from '../app-routes';
import { ProcessData } from './utils';
import { GoogleOAuthProvider } from '@react-oauth/google';

import './style.scss';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import 'moment/min/locales';
import { isBookFromPath, isChangePasswordFromPath, isCondoWeeklySpeccialMarketingFromPath, isReservationsFromPath, isSavedPropertiesFromPath } from '../../../share/utils/routes';
import { Base64 } from 'js-base64';

export function App() {
  const dispatch = useDispatch();

  const history = useHistory();

  if (isSpanish()) {
    moment.locale('es');
  }
  
  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const menuStore: IMenuState = useSelector(
    (state: FunctionalRootState) => state.navigationMenuStore,
  );

  const { account, userLanguage, isTokenExpired, user } = loginStore;

  const { items } = menuStore;
  
  const isPublicAccount = account?.isPublicAccountType;
  const isPrivateWithLoginAccount = account?.isPrivateLoginAccountType;
  const isPrivateWithTokenAccount = account?.isPrivateTokenAccountType;
  const fontFamily = account?.fontFamily;
  const secondaryFontFamily = account?.secondaryFontFamily;
  const generalBorderRadius = account?.generalBorderRadius;

  const isCondoWeeklySpecialMarketing = useMemo(() => isCondoWeeklySpeccialMarketingFromPath(history), [history]);

  const pagesToExcludeAccountCheck = useMemo(
    () => [
      isWidgetFromPath(history),
      isCondoWeeksFromPath(history),
      isCondoWorldFromPath(history),
      isErrorFromPath(history),
      isThreeDSFromPath(history),
    ],
    [history],
  );

  const isWidgetPage = useMemo(() => isWidgetFromPath(history), [history]);
  const isReservationCancellation = useMemo(
    () => isReservationCancellationFromPath(history),
    [history],
  );
  const isSignIn = useMemo(() => isSignInFromPath(history), [history]);

  const locale = getLocale(userLanguage);

  const messages = getMessages(locale);
  
  useEffect(() => {
    ProcessData(dispatch);

    if (!pagesToExcludeAccountCheck.includes(true)) {
      const pathAccountName = getAccountUsernameFromPath(history);
      if (!isSignInFromPath(history) && !isSignOutFromPath(history)) {
        const { pathname, search } = history.location;
        setLoginRedirect(`${pathname}${isEmpty(search) ? '' : search}`);
      }

      dispatch(
        getLoginUserAccount(
          pathAccountName,
          isWidgetPage,
          isCondoEngineFromPath(history),
          isAnyReservationCancelFromPath(history),
          isCondoWeeklySpecialMarketing,
          getCancelIdCancellationFromPath(history),
          getTempKeyCancellationFromPath(history),
        ),
      );
    }
  }, []);

  useEffect(() => {
    const loggedUser = localStorage.getItem(LOGGED_USER_LABEL);
    const userDataStorage = loggedUser ? JSON.parse(Base64.decode(loggedUser)) : null;
    if (account?.isAshevillePlay && user && user?.isAshevillePlayAdmin && !userDataStorage) {
      const isDetailPage = isDetailFromPath(history);
      const isChangePasswordPage = isChangePasswordFromPath(history);
      const isBookFromPathPage = isBookFromPath(history);
      const isSavedPropertiesPage = isSavedPropertiesFromPath(history);
      const isReservationsPage = isReservationsFromPath(history);

      if (isDetailPage || isChangePasswordPage || isBookFromPathPage || isSavedPropertiesPage || isReservationsPage) {
        const { pathname, search } = history.location;
        dispatch(setLoginRedirect(`${pathname}${isEmpty(search) ? '' : search}`));
        history.push(`/${account?.name}${Routes.Login}`);
      }
    }
  }, [account, user]);

  const handleExpireRedirect = () => {
    if (isPublicAccount) {
      window.location.reload();
    } else if (isPrivateWithLoginAccount) {
      dispatch(logout());
      history.push(`/${account.name}${Routes.Login}`);
    } else if (isPrivateWithTokenAccount) {
      const logoutUrlMenu = menuStore?.items?.logoutUrl;
      const logoutUrlStorage = localStorage.getItem(USER_BASE_URL);
      const logoutUrlToken = get(DecodeTokenData(), 'logoutUrl', null);
      const logoutPref = getPreferenceFromAccount(account, LOGOUT_FIELD_KEY);
      const logoutUrl = !isEmpty(logoutUrlStorage)
        ? logoutUrlStorage
        : !isEmpty(logoutUrlMenu)
        ? logoutUrlMenu
        : !isEmpty(logoutUrlToken)
        ? logoutUrlToken
        : logoutPref;

      Cookies.remove(COOKIE_TOKEN_NAME);
      Cookies.remove(COOKIE1_TOKEN_NAME);

      if (!isEmpty(logoutUrl)) {
        window.location.replace(logoutUrl);
      }
    }
  };

  const renderContent = () => {
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });

    // Check if modern template is being used to conditionally hide CurrencyCalculator
    const useModernTemplate = useModernTemplateCheck(account, items);

    return (
      <CookiesProvider>
        <ConfigProvider locale={getAntLocale(locale)}>
          <IntlProvider
            messages={getMessages(locale)}
            locale={locale}
            defaultLocale={locale}
          >
            <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID as string}>

            <div className={`app ${generalBorderRadius ? `border-radius-${generalBorderRadius}` : ''} ${isReservationCancellation ? 'cancellation' : ''}${fontFamily ? `${fontFamily}-font`: ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font`: ''}`}>
              <ApplicationHelmet />

              <ApplicationGoogleManager />

              <ModernSidebarGlobal>
                <ApplicationHeader />

                <ApplicationHeaderMessage />

                <ApplicationRoutes />

                <ApplicationFooter />

                {!useModernTemplate && <CurrencyCalculator />}
              </ModernSidebarGlobal>

              {!isReservationCancellation ? (
                <ToastContainer
                  position="top-right"
                  autoClose={6000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                ></ToastContainer>
              ) : null}

              {!isSignIn && isTokenExpired ? (
                <ExpiredSessionModal
                  visible={true}
                  hasRedirect={!isPublicAccount}
                  onHandleRedirect={handleExpireRedirect}
                />
              ) : null}
            </div>
            </GoogleOAuthProvider>
          </IntlProvider>
        </ConfigProvider>
      </CookiesProvider>
    );
  };

  if (
    (account?.type === LoginType.PrivateWithLogin && loginStore?.loading) ||
    (account?.type !== LoginType.PrivateWithLogin && !items && !pagesToExcludeAccountCheck.includes(true)) ||
    loginStore?.forceLoading
  ) {
    // Check if modern template is being used for loading message
    // Only show messages for specific site id and org id combination
    let useModernTemplate = false;
    
    // First check localStorage cache (from previous visit)
    const cached = localStorage.getItem('modern-template-active');
    
    if (account && items) {
      // Use the exported function to ensure consistency
      useModernTemplate = useModernTemplateCheck(account, items);
      
      // Cache the result in localStorage for next time
      localStorage.setItem('modern-template-active', String(useModernTemplate));
    } else if (cached !== null) {
      // Use cached value if we don't have full data yet
      useModernTemplate = cached === 'true';
    } else if (account?.isRSITemplate && account?.modernHomeTemplateOrgidList && account?.modernHomeTemplateSiteidList) {
      // If we have account with modern template lists configured, show optimistically
      // This handles first-time loads when items haven't loaded yet
      useModernTemplate = true;
    }
    
    return (
      <div className="loading-container">
        <Loading isModernTemplate={useModernTemplate} />
      </div>
    );
  } else {
    return renderContent();
  }
}
